import { useMutation, useQuery } from '@tanstack/react-query';
import { legacy } from '@/shared/api/client';
import { useAuth } from '@/shared/lib/auth';
import { normalizePointLogs } from '@/features/leads/model/lead-logs';
import type { Passbook, PointLog } from '@/shared/types';
import {
  buildAddPointsBody,
  buildRedemptionBody,
  type OperatorConfirmPayload,
} from '../model/operator-payload';
import {
  assertNotDuplicateCouponResponse,
  isAlreadyAwardedError,
  operatorErrorMessage,
} from '../model/operator-errors';

function headers(idPassbook: string) {
  return { headers: { 'X-Passbook-Id': idPassbook } };
}

export type ComboPhase = 'idle' | 'redeeming' | 'awarding' | 'done' | 'partial';

export interface ComboResult {
  phase: ComboPhase;
  redemptionOk: boolean;
  awardOk: boolean;
  /** true se o leg de resgate foi de fato executado. */
  redemptionRan: boolean;
  /** true se o leg de lançamento foi de fato executado. */
  awardRan: boolean;
  /** Resposta bruta do removePoints (recibo giftcard). */
  redemptionData?: unknown;
  errorMessage?: string;
}

type LegacyPost = (path: string, body: unknown, opts?: { headers?: Record<string, string> }) => Promise<unknown>;

/**
 * Selo (`/send` award, `/resgate` redemption) não notifica/atualiza wallet sozinho —
 * ao contrário de `/transaction/addPoints|removePoints`, que já dispara a notificação
 * internamente. O dash legado sempre encadeia um `POST /token` logo depois; replicamos
 * aqui. Best-effort: falha aqui não pode virar erro da operação, o crédito já aconteceu.
 */
async function notifyStampsWallet(
  post: LegacyPost,
  hdr: { headers: Record<string, string> },
  params: { serialNumber: string; idPassbook: string; countStamps?: number; resgate?: boolean },
): Promise<void> {
  try {
    await post(
      '/token',
      {
        serialNumber: params.serialNumber,
        idPassbook: params.idPassbook,
        ...(params.countStamps != null ? { count_stamps: params.countStamps } : {}),
        ...(params.resgate ? { resgate: true } : {}),
      },
      hdr,
    );
  } catch (err) {
    console.error('Falha ao notificar selo (POST /token):', err);
  }
}

/**
 * Combo puro (testável): resgate → lançamento.
 * phase:'partial' só se redemptionRan && lançamento falhou (BUG 2).
 * awardUnits === 0 (resgateNaoPontua) → não chama addPoints.
 */
export async function runOperatorCombo(opts: {
  idPassbook: string;
  passbook: Passbook;
  payload: OperatorConfirmPayload;
  user: { _id?: string; name?: string };
  post?: LegacyPost;
}): Promise<ComboResult> {
  const post = opts.post ?? ((path, body, cfg) => legacy.post(path, body, cfg));
  const { idPassbook, passbook, payload, user } = opts;
  const hdr = headers(idPassbook);

  let redemptionRan = false;
  let awardRan = false;
  let redemptionOk = false;
  let awardOk = false;
  let redemptionData: unknown;

  try {
    if (payload.redemptionEnabled) {
      const red = buildRedemptionBody(payload, user, passbook);
      if (red.kind === 'resgate') {
        redemptionData = await post('/resgate', red.body, hdr);
        await notifyStampsWallet(post, hdr, { serialNumber: payload.serialNumber, idPassbook, resgate: true });
      } else if (red.kind === 'removePoints') {
        redemptionData = await post('/transaction/removePoints', red.body, hdr);
      }
      redemptionRan = true;
      redemptionOk = true;
    }

    const add = buildAddPointsBody(payload, user, passbook);
    // Defesa: nunca tratar como sucesso um noop quando havia ganho a lançar.
    if (payload.awardUnits > 0 && (add.kind === 'noop' || !add.body)) {
      throw new Error(
        'Falha ao montar lançamento de fidelidade. Recarregue e tente novamente.',
      );
    }
    if (add.kind === 'noop' || payload.awardUnits <= 0) {
      awardOk = true;
    } else if (add.kind === 'send') {
      await post('/send', add.body, hdr);
      await notifyStampsWallet(post, hdr, {
        serialNumber: payload.serialNumber,
        idPassbook,
        countStamps: payload.awardUnits,
      });
      awardRan = true;
      awardOk = true;
    } else if (add.kind === 'addPoints') {
      const awardData = await post('/transaction/addPoints', add.body, hdr);
      // Soft-fail legado: HTTP 200 + status 105 "duplicate ignored" (E11000).
      assertNotDuplicateCouponResponse(awardData);
      awardRan = true;
      awardOk = true;
    }

    return {
      phase: 'done',
      redemptionOk,
      awardOk,
      redemptionRan,
      awardRan,
      redemptionData,
    };
  } catch (err) {
    if (redemptionRan && !awardOk) {
      return {
        phase: 'partial',
        redemptionOk: true,
        awardOk: false,
        redemptionRan: true,
        awardRan,
        redemptionData,
        errorMessage: operatorErrorMessage(err),
      };
    }
    throw err;
  }
}

export function useOperatorCombo(idPassbook: string, passbook: Passbook) {
  const { user } = useAuth();
  return useMutation({
    mutationFn: (payload: OperatorConfirmPayload) =>
      runOperatorCombo({
        idPassbook,
        passbook,
        payload,
        user: user ?? {},
      }),
  });
}

/** Retry do lançamento — mesmo externalId; 105 = já lançado = sucesso. */
export function useOperatorAwardRetry(idPassbook: string, passbook: Passbook) {
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (payload: OperatorConfirmPayload) => {
      const add = buildAddPointsBody(payload, user ?? {}, passbook);
      if (add.kind === 'noop' || payload.awardUnits <= 0) return;
      try {
        if (add.kind === 'send') {
          await legacy.post('/send', add.body, headers(idPassbook));
          await notifyStampsWallet(legacy.post, headers(idPassbook), {
            serialNumber: payload.serialNumber,
            idPassbook,
            countStamps: payload.awardUnits,
          });
        } else if (add.kind === 'addPoints') {
          const awardData = await legacy.post(
            '/transaction/addPoints',
            add.body,
            headers(idPassbook),
          );
          assertNotDuplicateCouponResponse(awardData);
        }
      } catch (err) {
        if (isAlreadyAwardedError(err)) return;
        throw err;
      }
    },
  });
}

export function useOperatorRecentActivity(idPassbook: string, serialNumber: string | undefined) {
  return useQuery({
    queryKey: ['operator', 'activity', idPassbook, serialNumber],
    enabled: Boolean(serialNumber),
    queryFn: async (): Promise<PointLog[]> => {
      const raw = await legacy.get<unknown>(`/log/${serialNumber}`, headers(idPassbook));
      return normalizePointLogs(raw).slice(0, 5);
    },
    // Tela de operador: nunca serve atividade recente velha — sempre busca de novo.
    staleTime: 0,
    gcTime: 0,
  });
}
